import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { PhaseId } from '@/common/types/scopeAndSequence';
import { Student } from '@/common/types/student';
import InitialsCircle from '@/common/ui/InitialsCircle';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { getPhaseById } from '@/common/utils/scopeAndSequence';
import {
  ChalkboardTeacherIcon,
  PlusIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@shadcn/components/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from '@shadcn/components/chart';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';

interface DataPoint {
  level: string;
  phase: string;
  students: Student[];
}

interface LearnerLevelDistributionProps {
  students: Student[];
}

export default function LearnerLevelDistribution({
  students,
}: LearnerLevelDistributionProps) {
  const navigate = useNavigate();

  const [selectedBar, setSelectedBar] = useState<DataPoint | null>(null);

  const chartData = useMemo(() => {
    // build raw columns with student lists
    const columns = scopeAndSequence.levels.map((level) => ({
      level: level.level.toString(),
      phase:
        getPhaseById(level.phase as PhaseId)?.phase.toString() ?? 'Unknown',
      students: students.filter((s) => s.level === level.level),
    }));

    const data: DataPoint[] = [];
    for (let i = 0; i < columns.length; ) {
      if (columns[i].students.length === 0) {
        // find end of this empty run within the same phase
        let j = i + 1;
        while (
          j < columns.length &&
          columns[j].students.length === 0 &&
          columns[j].phase === columns[i].phase
        ) {
          j++;
        }
        const runLength = j - i;
        if (runLength > 2) {
          // compress into "a~b"
          const start = columns[i].level;
          const end = columns[j - 1].level;
          data.push({
            level: `${start}~${end}`,
            phase: columns[i].phase,
            students: [],
          });
        } else {
          // too short to compress, keep individually
          for (let k = i; k < j; k++) {
            data.push(columns[k]);
          }
        }
        i = j; // skip past this run
      } else {
        // non-empty, just include
        data.push(columns[i]);
        i++;
      }
    }

    return data;
  }, [students]);

  const phases = Array.from(new Set(chartData.map((d) => d.phase)));

  // pick some palette
  const palette = ['#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#a4de6c'];

  const phaseColors = phases.reduce<Record<string, string>>((acc, phase, i) => {
    acc[phase] = palette[i % palette.length];
    return acc;
  }, {});

  const chartConfig = phases.reduce<ChartConfig>(
    (acc, phase) => ({
      ...acc,
      [phase]: {
        label: `Phase ${phase}`,
        color: phaseColors[phase],
      },
    }),
    {},
  );

  function handleGenerateLessonPlan() {
    if (!selectedBar) return;
    trackMixpanelEvent('Generate Lesson Plan from Insights', {
      level: selectedBar.level,
    });
    navigate(`/dashboard/lessonplans/new?level=${selectedBar.level}`);
  }

  const CustomTooltip = ({ active, payload }: any) => {
    const dataPoint = payload?.[0]?.payload;
    if (!active || !dataPoint) return null;

    const students = dataPoint.students;
    const level = scopeAndSequence.levels.find(
      (l) => l.level === parseInt(dataPoint.level),
    );

    return (
      <div className="rounded bg-white py-2 p-4 flex flex-col gap-2 shadow text-sm">
        <div className="flex flex-col">
          <p className="font-medium text-muted-foreground">
            Phase {dataPoint.phase}
          </p>
          <p className="font-medium">
            Level{dataPoint.level.includes('~') ? 's' : ''} {dataPoint.level} -{' '}
            {level?.description ?? ''}
          </p>
        </div>
        {students.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-muted-foreground">{students.length} learners</p>
            <ul className="flex flex-col gap-1">
              {students.slice(0, 5).map((s: Student) => (
                <li key={s.id} className="flex items-center gap-1">
                  <InitialsCircle
                    userName={`${s.studentFirstName} ${s.studentSurname}`}
                    size={24}
                  />
                  {s.studentFirstName} {s.studentSurname}
                </li>
              ))}
              {students.length > 5 && (
                <li className="text-muted-foreground ml-1 flex items-center gap-1">
                  <PlusIcon className="w-3 h-3" /> {students.length - 5} more
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="w-full relative">
      <CardHeader>
        <CardTitle>Learner Level Distribution</CardTitle>
        <CardDescription className="flex flex-col gap-2">
          <span>
            The distribution of learners' levels across the phases of the Scope
            & Sequence
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {students.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <UsersIcon size={48} className="text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">No Learners Selected</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Select learners to see their distribution across the Scope &
              Sequence.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 items-center">
            <ChartContainer
              config={chartConfig}
              className="min-h-[200px] max-h-[600px] w-full"
            >
              <BarChart
                accessibilityLayer
                data={chartData}
                margin={{ bottom: 40 }}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  xAxisId={0}
                  dataKey="level"
                  tickFormatter={(value) => value.toString()}
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  label={{
                    value: 'Scope & Sequence Level',
                    position: 'insideBottom',
                    offset: -28,
                    fontSize: 16,
                  }}
                />
                <YAxis
                  dataKey={(entry) => entry.students.length}
                  tickFormatter={(value) => value.toString()}
                  tickLine={false}
                  tickMargin={10}
                  allowDecimals={false}
                  axisLine={false}
                  label={{
                    value: 'Learners',
                    position: 'insideLeft',
                    offset: 12,
                    fontSize: 16,
                    angle: -90,
                  }}
                />
                <ChartTooltip content={<CustomTooltip />} />
                <Bar
                  dataKey={(entry) => entry.students.length}
                  radius={4}
                  onClick={(e) => {
                    trackMixpanelEvent('View Level Details in Insights', {
                      level: e.payload.level,
                    });
                    setSelectedBar(e.payload);
                  }}
                >
                  {chartData.map((entry, idx) => (
                    <Cell
                      className="cursor-pointer"
                      key={`cell-${idx}`}
                      fill={phaseColors[entry.phase] ?? '#ccc'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>

            <div className="flex flex-wrap gap-3 items-end">
              {phases.map((phase) => (
                <div key={phase} className="flex items-center gap-1">
                  <div
                    className={`w-2 h-2 rounded-full`}
                    style={{ backgroundColor: phaseColors[phase] }}
                  />
                  <p className="text-sm text-muted-foreground">Phase {phase}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {selectedBar && (
        <Dialog
          open={!!selectedBar}
          onOpenChange={(open) => !open && setSelectedBar(null)}
        >
          <DialogContent>
            <DialogHeader>
              <span className="text-sm font-medium text-muted-foreground">
                Phase {selectedBar.phase}
              </span>
              <DialogTitle>
                Level{selectedBar.level.includes('~') ? 's' : ''}{' '}
                {selectedBar.level} -{' '}
                {scopeAndSequence.levels.find(
                  (l) => l.level === parseInt(selectedBar.level),
                )?.description ?? ''}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription>
              <div className="my-2 grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                {selectedBar.students.map((s: Student) => (
                  <div key={s.id} className="flex items-center gap-1">
                    <InitialsCircle
                      userName={`${s.studentFirstName} ${s.studentSurname}`}
                      size={24}
                    />
                    {s.studentFirstName} {s.studentSurname}
                  </div>
                ))}
              </div>
            </DialogDescription>
            <DialogFooter className="justify-between">
              <Button
                className="gap-2 w-full"
                onClick={handleGenerateLessonPlan}
              >
                <ChalkboardTeacherIcon size={20} />
                Generate Lesson Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
